import './TreeGrid.TypeScript.API.d.ts';
import './TreeGrid.TypeScript.React.d.ts';

import { Typography } from '@mui/material';
import React from 'react';

import { getBearerToken } from '../../services/workbook.service';
import { ISpreadsheetGrid, TreeGridSS } from './ISpreadsheetGrid';
import { SelectionSummaryStyled } from './Spreadsheet.styles';


type SpreadsheetProps = {
    spreadsheetUrl: string,
    exportFilename: string,
    hasUnsavedChanges: boolean,
    onToolbarButtonClick: (buttonName: string) => void,
    onRenderFinish: (grid: ISpreadsheetGrid, sheetName: string|undefined) => void,
    onStartEdit: (row: number, col: string) => boolean,
    onEndEdit: (row: number, col: string, save: boolean, value: string) => boolean,
    onColsAdd: (cols: string[], toCol: string, right: boolean, empty: boolean) => boolean,
    onRowAdd: (parentRow: number) => boolean,
    onCanColDelete: (col: string) => boolean,
    onCanRowDelete: (row: number) => boolean,
    onCanPaste: (startCol: string, startRow: number, numOfCols: number, numOfRows: number) => boolean,
    onSave: (fileBlob: Blob) => void,
};

type SelectionSummary = { 
    count: number, 
    sum: number, 
    avgCount: number,
    displayType: 'Clear' | 'Count Only' | 'All'
}

export class Spreadsheet extends React.Component<SpreadsheetProps> {
    state: {
        exportAsSave: boolean, 
        selectionSummary: SelectionSummary
    } = { 
        exportAsSave: false, 
        selectionSummary: { count: 0, sum: 0, avgCount: 0, displayType: 'Clear' }
    };

    grid: TGrid;
    spreadsheetUrl: string;

    constructor(public props : SpreadsheetProps) {
        super(props);

        const { 
            spreadsheetUrl, 
            exportFilename, 
            hasUnsavedChanges,
            onToolbarButtonClick, 
            onRenderFinish, 
            onStartEdit, 
            onEndEdit, 
            onColsAdd, 
            onRowAdd,
            onCanColDelete,
            onCanRowDelete, 
            onCanPaste,
            onSave 
        } =  this.props;

        this.spreadsheetUrl = spreadsheetUrl;

        window.Grids.OnLoadError = function(grid:any) {  
            // eslint-disable-next-line no-console
            console.log(grid);
        };

        Grids.OnRenderFinish = (grid: TGrid) => { 
            const ssGrid: ISpreadsheetGrid = new TreeGridSS(grid);

            //@ts-ignore
            onRenderFinish(ssGrid, grid?.xP);
        };

        Grids.OnExport = (_grid: TGrid, dataBlob: any, type: number | boolean) => {  
            const { exportAsSave } = this.state;

            if(exportAsSave) {
                onSave(dataBlob);
            } else {
                downloadFile(dataBlob, exportFilename);
            }

            this.setState({ exportAsSave: false });
            return true;
        };

        Grids.OnSave = (grid: TGrid, row: TRow, autoupdate: number | boolean): number | boolean | void | null | undefined => {
            this.setState({ exportAsSave: true });

            setTimeout(() => {
                // TG doesn't let you call an actiona within a action, so we need to run this async
                grid?.ActionExport(false, false);
            }, 1);

            return true;
        };

        Grids.OnClick = (_grid: TGrid, row: TRow, col: string, x: number, y: number): number | boolean | void | null | undefined => {
            if(row.Pos ) { // check for toolbar button click
                onToolbarButtonClick(col);
            }
        };


        Grids.OnStartEdit = (grid: TGrid, row: TRow, col: string): number | boolean | void | null | undefined => {
            const rowIndex = grid?.GetRowIndex(row);

            return onStartEdit(rowIndex, col);
        };

        Grids.OnEndEdit = (grid: TGrid, row: TRow, col: string, save: number | boolean, _val: any, value: string) => {
            const rowIndex = grid?.GetRowIndex(row);

            return onEndEdit(rowIndex, col, Boolean(save), value);
        };

        Grids.OnColsAdd = (_grid: TGrid, cols: string[], tocol: string, right: number | boolean, empty: number | boolean): number | boolean | void | null | undefined => {
            return onColsAdd(cols, tocol, right ? true : false, empty ? true : false);
        };

        Grids.OnCanRowAdd = (grid: TGrid, parent: TRow, next: TRow): number | boolean | void | null | undefined => {
            const rowIndex = grid?.GetRowIndex(next);

            return onRowAdd(rowIndex);
        };

        Grids.OnCanColDelete = (_grid: TGrid, col: string, type: number, cols: string[]): number | boolean | void | null | undefined => {
            const canDel = onCanColDelete(col);

            return canDel ? 2 : 0;
        };

        Grids.OnCanRowDelete = (grid: TGrid, row: TRow, type: number, rows: TRow[]): number | void | null | undefined => {
            const rowIndex = grid?.GetRowIndex(row);
            const canDel = onCanRowDelete(rowIndex);

            return canDel ? 2 : 0;
        };

        Grids.OnPaste = (grid: TGrid, pastedtext: string[], cols: string[]): number | boolean | void | null | undefined => {
            if(!grid) return;

            const numOfCols = cols[0].split('\t').length;
            const numOfRows = cols.length;

            const startRow = grid.GetRowIndex(grid.ARow);
            const startCol = grid.ACol;

            const canPaste = onCanPaste(startCol, startRow, numOfCols, numOfRows);

            return canPaste;
        };

        Grids.OnMoveFocus = (_grid: TGrid, rect: any[], orect: any[], Values: any[], Rows: TRow[], Cols: string[], SourceRows: TRow[], SourceCols: string[]): any => {
            return true;
        };

        Grids.OnSelect = (grid: TGrid, row: TRow, deselect: number | boolean, Cols: string[], test: number | boolean): number | boolean | void | null | undefined => {
            setTimeout(function(){calculateSummaryValues(grid as TTGrid);}, 10);
        };

        const calculateSummaryValues = (grid: TTGrid) => {
            setSummaryInformation();
      
            const summaryModel: SelectionSummary = { count: 0, sum: 0, avgCount: 0, displayType: 'Clear' };
            const  selRanges = grid.GetSelRanges() as any[];

            for(let rangeIdx=0; rangeIdx < selRanges.length;  rangeIdx++){
               const cellRange = selRanges[rangeIdx];
      
               calculateRectValues(
                grid, cellRange[0].Index, cellRange[2].Index, cellRange[1], cellRange[3], summaryModel);
            }
      
            if(summaryModel.count > 1){
               if(summaryModel.avgCount > 0){
                  const average = summaryModel.sum/summaryModel.avgCount;

                  setSummaryInformation(summaryModel.count, average, summaryModel.sum);
               } else {
                  setSummaryInformation(summaryModel.count);
               }
            }
        };

        const calculateRectValues = (
                grid: TTGrid, startRowIdx:number, endRowIdx:number, startCol:string, endCol:string,
                model: SelectionSummary) => { 

            const startColIdx:number = grid.GetColIndex(startCol);
            const endColIdx:number = grid.GetColIndex(endCol);
    
            for(let collIdx= startColIdx; collIdx <= endColIdx; collIdx++){
                const colIdxStr = grid.GetColByIndex(collIdx, 0);
    
                for (let rowIdx = startRowIdx; rowIdx <= endRowIdx; rowIdx++) {
                    const cellVal = grid.GetValue(grid.Rows[rowIdx], colIdxStr);
        
                    if(cellVal.toString().trim() !== "")
                        model.count++;
        
                    const numVal = getCellNumber(grid, grid.Rows[rowIdx], colIdxStr);

                    if(!isNaN(numVal)){
                        model.sum+=numVal;
                        model.avgCount++;
                    }
                }
            }
        };

        const getCellNumber = (grid:TTGrid, row:TRow, colIdxStr:string) : number => {
            let cellStr:string = grid.GetValue(row, colIdxStr);

            cellStr = cellStr.toString().replace(",", "").trim();
      
            // check is negative
            if(cellStr.indexOf('(') === 0 && cellStr.indexOf(')') === cellStr.length-1){
               cellStr = cellStr.replace("(", "");
               cellStr = cellStr.replace(")", "");
               cellStr = `-${cellStr}`;
            }
      
            return parseFloat(cellStr);
         };

        const setSummaryInformation = (count?: number, average?: number, sum?: number) => {
            const selectionSummary: SelectionSummary = { count: 0, sum: 0, avgCount: 0, displayType: 'Clear' };
            
            if(!isNaN(average as number) || !isNaN(count as number) || !isNaN(sum as number)) {
                if(!isNaN(average as number) && !isNaN(count as number) && !isNaN(sum as number)) {
                    selectionSummary.count = count!;
                    selectionSummary.sum = sum!;
                    selectionSummary.avgCount = average!;
                    selectionSummary.displayType = 'All';
                } else {
                    selectionSummary.count = count ? count : 0;
                    selectionSummary.displayType = 'Count Only';
                }
            }

            this.setState({ selectionSummary: selectionSummary });
        };

        function downloadFile(blob: Blob, filename: string) {
            const anchor = document.createElement('a');

            anchor.href = URL.createObjectURL(blob);
            anchor.download = filename;
            anchor.style.display = "none";
        
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
        }
    }

    componentDidMount() {
        window.StartTreeGrid(); 
    }

    componentWillUnmount() {
        window.DisposeGrids();
    }

    render() {
        const { selectionSummary: summary } = this.state;
        const { hasUnsavedChanges } = this.props;

        return (
            <div>
                <div id="treegrid_main_tag" style={ { height: "750px",  zoom:1 } } >
                    { /* <bdo> tag is used instead of <treegrid> tag to avoid TypeScript error for custom tag */ }
                    {/* By the id value the grid is accessible for global API like window.Grids.SampleGrid.Reload(); */}
                    <bdo 
                        id="excel_spreadsheet" 
                        debug="problem" suppressmessage="4"
                        layout_url="/Layouts/ExcelDef.xml" layout_static="ExcelDef"
                        exportpdf_url='' exportpdf_data='Data'  exportpdf_type='settings'
                        //@ts-ignore
                        data_header_authorization={getBearerToken()}
                        data_url={this.spreadsheetUrl} data_format="xlsx" 
                        upload_url="/" upload_xml="2" upload_type="Changes,Cells,Body,Data,All"
                        source="Defaults+Text,Base,Layout;Data"
                    />
                </div>
                <div>
                    <SelectionSummaryStyled>
                        <Typography variant="subtitle1">
                        {
                            summary.displayType === 'All' ?
                                <div>
                                    <span style={ { marginRight: "25px" } }>{ `Average: ${summary.avgCount.toFixed(2)}` }</span>
                                    <span style={ { marginRight: "25px" } }>{ `Count: ${summary.count}` }</span>
                                    <span>{ `Sum: ${summary.sum.toFixed(2)}` }</span>
                                </div> 
                            : summary.displayType === 'Count Only' ? 
                                <span style={ { marginRight: "25px" } }>{ `Count: ${summary.count}` }</span>
                            : null
                        }
                        </Typography>
                    </SelectionSummaryStyled>
                </div>
            </div>
        );
    }
}    